import React, { useState, useEffect } from "react";
import {
    makeStyles,
    Paper,
    Button,
    Divider,
    Typography,
    Grid,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    FormControl,
    InputLabel,
    MenuItem,
    Select
} from "@material-ui/core";
import { Formik, Form, Field, FieldArray } from 'formik';
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import {
    Edit as EditIcon,
    DeleteOutline as DeleteOutlineIcon,
    Add as AddIcon
} from "@material-ui/icons";

import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import {
    PLAN_RECORD_PADRAO,
    planRecordFrom,
    novoAddon,
    PLAN_FEATURES,
    ADDON_TYPES,
    ADDON_UNITS,
    addonTotal,
    addonsTotal
} from "./planRecord";
import { i18n } from "../../translate/i18n";


const useStyles = makeStyles(theme => ({
    root: {
        width: '100%'
    },
    mainPaper: {
        width: '100%',
        flex: 1,
        padding: theme.spacing(2)
    },
    fullWidth: {
        width: '100%'
    },
    tableContainer: {
        width: '100%',
        overflowX: "scroll",
        ...theme.scrollbarStyles
    },
    textfield: {
        width: '100%'
    },
    textRight: {
        textAlign: 'right'
    },
    row: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2)
    },
    control: {
        paddingRight: theme.spacing(1),
        paddingLeft: theme.spacing(1)
    },
    buttonContainer: {
        textAlign: 'right',
        padding: theme.spacing(1)
    },
    divider: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    // Tracejado e em largura toda, como no mockup do caso: a borda aberta diz
    // que a lista continua, em vez de parecer um botão de ação concluída.
    addButton: {
        width: '100%',
        borderStyle: 'dashed',
        borderWidth: 1,
        borderColor: theme.palette.divider,
        padding: theme.spacing(1.5)
    }
}));

/**
 * Campo de recurso do plano: habilitado ou desabilitado.
 *
 * Existe porque os onze recursos do plano tinham o mesmo bloco de dez linhas
 * copiado, e era nele que o rótulo ficava escrito em português dentro do JSX.
 */
function CampoDeRecurso({ name, label, classes }) {
    const id = `${name}-selection`

    return (
        <Grid xs={12} sm={6} md={2} item>
            <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor={id}>{label}</InputLabel>
                <Field
                    as={Select}
                    id={id}
                    label={label}
                    labelId={`${id}-label`}
                    name={name}
                    margin="dense"
                >
                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
            </FormControl>
        </Grid>
    )
}

/** Limite numérico do plano (usuários, conexões, filas, GB). */
function CampoNumerico({ name, label, classes, min = 0, disabled }) {
    return (
        <Grid xs={12} sm={6} md={2} item>
            <Field
                as={TextField}
                id={`plan-${name}`}
                label={label}
                name={name}
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
                type="number"
                inputProps={{ min }}
                disabled={disabled}
            />
        </Grid>
    )
}

export function PlanManagerForm(props) {
    const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
    const classes = useStyles()

    const [record, setRecord] = useState(PLAN_RECORD_PADRAO)

    useEffect(() => {
        setRecord(initialValue)
    }, [initialValue])

    const handleSubmit = async (data) => {
        onSubmit(data)
    }

    return (
        <Formik
            enableReinitialize
            className={classes.fullWidth}
            initialValues={record}
            onSubmit={(values, { resetForm }) =>
                setTimeout(() => {
                    handleSubmit(values)
                    resetForm()
                }, 500)
            }
        >
            {(values) => (
                <Form className={classes.fullWidth}>
                    <Grid spacing={1} justifyContent="flex-start" container>

                        {/* IDENTIFICACAO E CONTRATACAO */}
                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="status-selection">{i18n.t("plans.form.public")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="status-selection"
                                    label={i18n.t("plans.form.public")}
                                    labelId="status-selection-label"
                                    name="isPublic"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.yes")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.no")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="trial-selection">{i18n.t("plans.form.trial")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="trial-selection"
                                    label={i18n.t("plans.form.trial")}
                                    labelId="trial-selection-label"
                                    name="trial"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.yes")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.no")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* Dias de trial só faz sentido com trial ligado. */}
                        <CampoNumerico
                            name="trialDays"
                            label={i18n.t("plans.form.trialDays")}
                            classes={classes}
                            min={1}
                            disabled={!values.values.trial}
                        />

                        <Grid xs={12} sm={6} md={2} item>
                            <Field
                                as={TextField}
                                id="plan-name"
                                label={i18n.t("plans.form.name")}
                                name="name"
                                variant="outlined"
                                className={classes.fullWidth}
                                margin="dense"
                            />
                        </Grid>

                        <Grid xs={12} sm={6} md={2} item>
                            <Field
                                as={TextField}
                                id="plan-amount"
                                label={i18n.t("plans.form.amount")}
                                name="amount"
                                variant="outlined"
                                className={classes.fullWidth}
                                margin="dense"
                                placeholder="0,00"
                            />
                        </Grid>

                        <Grid xs={12} sm={6} md={2} item />

                        {/* LIMITES DO PLANO */}
                        <CampoNumerico name="users" label={i18n.t("plans.form.users")} classes={classes} />
                        <CampoNumerico name="connections" label={i18n.t("plans.form.connections")} classes={classes} />
                        <CampoNumerico name="queues" label={i18n.t("plans.form.queues")} classes={classes} />
                        <CampoNumerico name="storageGb" label={i18n.t("plans.form.storageGb")} classes={classes} />

                        <Grid xs={12} sm={6} md={4} item />

                        {/* RECURSOS */}
                        {PLAN_FEATURES.map(recurso => (
                            <CampoDeRecurso
                                key={recurso}
                                name={recurso}
                                label={i18n.t(`plans.form.features.${recurso}`)}
                                classes={classes}
                            />
                        ))}

                    </Grid>

                    {/* CAMPOS ADICIONAIS (#4377) */}
                    <Divider className={classes.divider} />
                    <Typography variant="subtitle2">
                        {i18n.t("plans.form.addons.title")}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" component="div" gutterBottom>
                        {i18n.t("plans.form.addons.subtitle")}
                    </Typography>

                    <FieldArray name="addons">
                        {({ push, remove }) => (
                            <>
                                {(values.values.addons || []).map((addon, index) => (
                                    <Grid spacing={1} container key={addon.id || "novo-" + index} alignItems="center">
                                        {/* TIPO */}
                                        <Grid xs={12} sm={6} md={2} item>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel htmlFor={"addon-type-" + index}>
                                                    {i18n.t("plans.form.addons.type")}
                                                </InputLabel>
                                                <Field
                                                    as={Select}
                                                    id={"addon-type-" + index}
                                                    label={i18n.t("plans.form.addons.type")}
                                                    name={"addons." + index + ".type"}
                                                    margin="dense"
                                                >
                                                    {ADDON_TYPES.map(tipo => (
                                                        <MenuItem value={tipo} key={tipo}>
                                                            {i18n.t("plans.form.addons.types." + tipo)}
                                                        </MenuItem>
                                                    ))}
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* DESCRICAO */}
                                        <Grid xs={12} sm={6} md={3} item>
                                            <Field
                                                as={TextField}
                                                id={"addon-description-" + index}
                                                label={i18n.t("plans.form.addons.description")}
                                                name={"addons." + index + ".description"}
                                                variant="outlined"
                                                className={classes.fullWidth}
                                                margin="dense"
                                            />
                                        </Grid>

                                        {/* QUANTIDADE */}
                                        <Grid xs={6} sm={3} md={1} item>
                                            <Field
                                                as={TextField}
                                                id={"addon-quantity-" + index}
                                                label={i18n.t("plans.form.addons.quantity")}
                                                name={"addons." + index + ".quantity"}
                                                variant="outlined"
                                                className={classes.fullWidth}
                                                margin="dense"
                                                type="number"
                                                inputProps={{ min: 1 }}
                                            />
                                        </Grid>

                                        {/* UNIDADE */}
                                        <Grid xs={6} sm={3} md={1} item>
                                            <FormControl margin="dense" variant="outlined" fullWidth>
                                                <InputLabel htmlFor={"addon-unit-" + index}>
                                                    {i18n.t("plans.form.addons.unit")}
                                                </InputLabel>
                                                <Field
                                                    as={Select}
                                                    id={"addon-unit-" + index}
                                                    label={i18n.t("plans.form.addons.unit")}
                                                    name={"addons." + index + ".unit"}
                                                    margin="dense"
                                                >
                                                    {ADDON_UNITS.map(unidade => (
                                                        <MenuItem value={unidade} key={unidade}>{unidade}</MenuItem>
                                                    ))}
                                                </Field>
                                            </FormControl>
                                        </Grid>

                                        {/* VALOR UNITARIO */}
                                        <Grid xs={6} sm={3} md={2} item>
                                            <Field
                                                as={TextField}
                                                id={"addon-unitAmount-" + index}
                                                label={i18n.t("plans.form.addons.unitAmount")}
                                                name={"addons." + index + ".unitAmount"}
                                                variant="outlined"
                                                className={classes.fullWidth}
                                                margin="dense"
                                                placeholder="0,00"
                                            />
                                        </Grid>

                                        {/* VALOR TOTAL — calculado, nunca digitado: total gravado que
                                            discorda de quantidade x valor unitario e defeito que
                                            ninguem consegue resolver meses depois. */}
                                        <Grid xs={6} sm={3} md={2} item>
                                            <TextField
                                                id={"addon-total-" + index}
                                                label={i18n.t("plans.form.addons.total")}
                                                value={i18n.t("plans.form.money") + " " + addonTotal(addon.quantity, addon.unitAmount).toLocaleString('pt-br', { minimumFractionDigits: 2 })}
                                                variant="outlined"
                                                className={classes.fullWidth}
                                                margin="dense"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </Grid>

                                        <Grid xs={12} md={1} item className={classes.textRight}>
                                            <IconButton
                                                onClick={() => remove(index)}
                                                aria-label={i18n.t("plans.form.addons.remove")}
                                                title={i18n.t("plans.form.addons.remove")}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                ))}

                                <Grid spacing={1} container alignItems="center">
                                    <Grid xs={12} sm={6} md={4} item>
                                        <Button
                                            className={classes.addButton}
                                            startIcon={<AddIcon />}
                                            onClick={() => push(novoAddon())}
                                        >
                                            {i18n.t("plans.form.addons.add")}
                                        </Button>
                                    </Grid>
                                    {(values.values.addons || []).length > 0 && (
                                        <Grid xs={12} sm={6} md={3} item>
                                            <Typography variant="body2" color="textSecondary">
                                                {i18n.t("plans.form.addons.sum")}
                                                {" "}
                                                {i18n.t("plans.form.money")}
                                                {" "}
                                                {addonsTotal(values.values.addons).toLocaleString('pt-br', { minimumFractionDigits: 2 })}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </>
                        )}
                    </FieldArray>

                    <Grid spacing={2} justifyContent="flex-end" container>

                        <Grid sm={3} md={2} item>
                            <ButtonWithSpinner className={classes.fullWidth} loading={loading} onClick={() => onCancel()} variant="contained">
                                {i18n.t("plans.form.clear")}
                            </ButtonWithSpinner>
                        </Grid>
                        {record.id !== undefined ? (
                            <Grid sm={3} md={2} item>
                                <ButtonWithSpinner className={classes.fullWidth} loading={loading} onClick={() => onDelete(record)} variant="contained" color="secondary">
                                    {i18n.t("plans.form.delete")}
                                </ButtonWithSpinner>
                            </Grid>
                        ) : null}
                        <Grid sm={3} md={2} item>
                            <ButtonWithSpinner className={classes.fullWidth} loading={loading} type="submit" variant="contained" color="primary">
                                {i18n.t("plans.form.save")}
                            </ButtonWithSpinner>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    )
}

export function PlansManagerGrid(props) {
    const { records, onSelect } = props
    const classes = useStyles()

    const renderWhatsapp = (row) => {
        return row.useWhatsapp === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderWhatsappOfficial = (row) => {
        return row.useWhatsappOfficial === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderFacebook = (row) => {
        return row.useFacebook === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderInstagram = (row) => {
        return row.useInstagram === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderCampaigns = (row) => {
        return row.useCampaigns === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderSchedules = (row) => {
        return row.useSchedules === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderInternalChat = (row) => {
        return row.useInternalChat === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderExternalApi = (row) => {
        return row.useExternalApi === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderKanban = (row) => {
        return row.useKanban === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };


    const renderIntegrations = (row) => {
        return row.useIntegrations === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderWavoip = (row) => {
        return row.wavoip === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    return (
        <Paper className={classes.tableContainer}>
            <Table
                className={classes.fullWidth}
                // size="small"
                padding="none"
                aria-label="a dense table"
            >
                <TableHead>
                    <TableRow>
                        <TableCell align="center" style={{ width: '1%' }}>#</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.trial")}</TableCell>
                        <TableCell align="left">{i18n.t("plans.form.name")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.users")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.public")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.connections")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.queues")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.storageGb")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.amount")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.features.useWhatsapp")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.features.useWhatsappOfficial")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.features.useFacebook")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.features.useInstagram")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.campaigns")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.schedules")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.features.useInternalChat")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.features.useExternalApi")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.features.useKanban")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.features.useIntegrations")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.features.wavoip")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell align="center" style={{ width: '1%' }}>
                                <IconButton onClick={() => onSelect(row)} aria-label="delete">
                                    <EditIcon />
                                </IconButton>
                            </TableCell>
                            <TableCell align="center">{row.trialDays || '-'}</TableCell>
                            <TableCell align="left">{row.name || '-'}</TableCell>
                            <TableCell align="center">{row.users || '-'}</TableCell>
                            <TableCell align="center">{row.isPublic ? "Sim" : "Não" || '-'}</TableCell>
                            <TableCell align="center">{row.connections || '-'}</TableCell>
                            <TableCell align="center">{row.queues || '-'}</TableCell>
                            <TableCell align="center">{row.storageGb || '-'}</TableCell>
                            <TableCell align="center">{i18n.t("plans.form.money")} {row.amount ? row.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00'}</TableCell>
                            <TableCell align="center">{renderWhatsapp(row)}</TableCell>
                            <TableCell align="center">{renderWhatsappOfficial(row)}</TableCell>
                            <TableCell align="center">{renderFacebook(row)}</TableCell>
                            <TableCell align="center">{renderInstagram(row)}</TableCell>
                            <TableCell align="center">{renderCampaigns(row)}</TableCell>
                            <TableCell align="center">{renderSchedules(row)}</TableCell>
                            <TableCell align="center">{renderInternalChat(row)}</TableCell>
                            <TableCell align="center">{renderExternalApi(row)}</TableCell>
                            <TableCell align="center">{renderKanban(row)}</TableCell>
                            <TableCell align="center">{renderIntegrations(row)}</TableCell>
                            <TableCell align="center">{renderWavoip(row)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    )
}

export default function PlansManager() {
    const classes = useStyles()
    const { list, save, update, remove } = usePlans()

    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [records, setRecords] = useState([])
    const [record, setRecord] = useState(PLAN_RECORD_PADRAO)

    useEffect(() => {
        async function fetchData() {
            await loadPlans()
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record])

    const loadPlans = async () => {
        setLoading(true)
        try {
            const planList = await list()
            setRecords(planList)
        } catch (e) {
            toast.error('Não foi possível carregar a lista de registros')
        }
        setLoading(false)
    }

    const handleSubmit = async (data) => {
        setLoading(true)
        console.log(data)
        try {
            if (data.id !== undefined) {
                await update(data)
            } else {
                await save(data)
            }
            await loadPlans()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação. Verifique se já existe uma plano com o mesmo nome ou se os campos foram preenchidos corretamente')
        }
        setLoading(false)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await remove(record.id)
            await loadPlans()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação')
        }
        setLoading(false)
    }

    const handleOpenDeleteDialog = () => {
        setShowConfirmDialog(true)
    }

    const handleCancel = () => {
        setRecord({ ...PLAN_RECORD_PADRAO, id: undefined })
    }

    const handleSelect = (data) => {
        setRecord(planRecordFrom(data))
    }

    return (
        <Paper className={classes.mainPaper} elevation={0}>
            <Grid spacing={2} container>
                <Grid xs={12} item>
                    <PlanManagerForm
                        initialValue={record}
                        onDelete={handleOpenDeleteDialog}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        loading={loading}
                    />
                </Grid>
                <Grid xs={12} item>
                    <PlansManagerGrid
                        records={records}
                        onSelect={handleSelect}
                    />
                </Grid>
            </Grid>
            <ConfirmationModal
                title="Exclusão de Registro"
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => handleDelete()}
            >
                Deseja realmente excluir esse registro?
            </ConfirmationModal>
        </Paper>
    )
}